import React, { useEffect, useState } from 'react';
import { Loader2, Save, IndianRupee, Percent } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { api, formatApiErrorDetail } from '../../lib/api';
import { ENTITY_KEYS, ENTITY_SCHEMAS } from './entitySchemas';
import { toast } from 'sonner';

const PRICED_SERVICES = ['flights', 'hotels', 'buses', 'trains', 'cabs', 'villas', 'packages', 'tours', 'visas', 'cruises', 'forex', 'insurance'];

const AdminSettings = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/admin/settings/pricing')
      .then((r) => setPricing(r.data))
      .catch(() => toast.error('Failed to load pricing'))
      .finally(() => setLoading(false));
  }, []);

  const updateField = (section, service, tier, value) => {
    setPricing((p) => ({
      ...p,
      [section]: {
        ...p[section],
        [service]: { ...p[section][service], [tier]: parseFloat(value) || 0 },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/pricing', {
        markup: pricing.markup,
        convenience_fee: pricing.convenience_fee,
      });
      toast.success('Pricing updated');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !pricing) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-red-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pricing & Markup</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Configure markup % and convenience fee per service. <span className="font-semibold">B2C</span> = retail customers; <span className="font-semibold">B2B</span> = business partners.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-pricing-btn" className="bg-red-600 hover:bg-red-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save Changes</>}
        </Button>
      </div>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Service</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider"><Percent className="h-3 w-3 inline" /> Markup B2C</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider"><Percent className="h-3 w-3 inline" /> Markup B2B</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider"><IndianRupee className="h-3 w-3 inline" /> Conv. Fee B2C</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider"><IndianRupee className="h-3 w-3 inline" /> Conv. Fee B2B</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {PRICED_SERVICES.map((svc) => (
                <tr key={svc} data-testid={`pricing-row-${svc}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">
                    {ENTITY_SCHEMAS[svc]?.label || svc}
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={pricing.markup?.[svc]?.b2c ?? 0}
                      data-testid={`markup-${svc}-b2c`}
                      onChange={(e) => updateField('markup', svc, 'b2c', e.target.value)}
                      className="w-24"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={pricing.markup?.[svc]?.b2b ?? 0}
                      data-testid={`markup-${svc}-b2b`}
                      onChange={(e) => updateField('markup', svc, 'b2b', e.target.value)}
                      className="w-24"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      value={pricing.convenience_fee?.[svc]?.b2c ?? 0}
                      data-testid={`fee-${svc}-b2c`}
                      onChange={(e) => updateField('convenience_fee', svc, 'b2c', e.target.value)}
                      className="w-24"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      value={pricing.convenience_fee?.[svc]?.b2b ?? 0}
                      data-testid={`fee-${svc}-b2b`}
                      onChange={(e) => updateField('convenience_fee', svc, 'b2b', e.target.value)}
                      className="w-24"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4 text-sm text-blue-900 dark:text-blue-200">
          <p className="font-semibold mb-1">How pricing works:</p>
          <p className="font-mono text-xs">total = (net_price × units) + markup% + (conv_fee × units)</p>
          <p className="mt-2">Changes apply instantly to all new flight searches and bookings.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
